import { Button } from "components/DesignSystem/Button/Button";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import Modal from "components/DesignSystem/Modal/Modal";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { FileInput } from "components/FileInput/FileInput";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { RightArrow } from "components/icons/RightArrow";
import { MM_DD_YYYY } from "constants/date";
import { PDF } from "constants/fileTypes";
import dayjs, { Dayjs } from "dayjs";
import { Form, Formik } from "formik";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { useHistory } from "react-router-dom";
import { useUploadAiReconcileStatementMutation } from "store/apis/reconciliation";
import { mixed, object, string } from "yup";
import { STRIPE_STATEMENT_ACCEPTED_FILES } from "./constants";
import { ReconciliationHeader } from "./ReconciliationTxn";

type StripeReconcileValues = {
  reconcilation_starting_date: string;
  reconcilation_ending_date: string;
  csv: File | null;
};

// Stripe accounts are reconciled only against the Stripe balance summary PDF
export const StripeReconcile = ({
  isOpen,
  onClose,
  bankAccountId,
  brandName,
  accountName,
  periodStart,
}: {
  isOpen: boolean;
  onClose: () => void;
  bankAccountId: string;
  brandName: string;
  accountName: string;
  // the day after the last reconciliation, or the reconciliation start date
  periodStart: Dayjs;
}) => {
  const { alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const history = useHistory();
  const query = useQuery();
  const [uploadStatement] = useUploadAiReconcileStatementMutation();

  const onSubmit = async (values: StripeReconcileValues) => {
    try {
      const data = await uploadStatement({
        groupId,
        entityId,
        payload: {
          entity_bank_account_id: bankAccountId,
          reconcilation_starting_date: dayjs(
            values.reconcilation_starting_date
          ).format(MM_DD_YYYY),
          reconcilation_ending_date: dayjs(
            values.reconcilation_ending_date
          ).format(MM_DD_YYYY),
          csv: values.csv!,
        },
      }).unwrap();
      history.push(
        `/books/ai-reconciliation/${
          data.reconciliation_state_id
        }?${query.toString()}`
      );
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Formik<StripeReconcileValues>
        initialValues={{
          reconcilation_starting_date: periodStart.format(MM_DD_YYYY),
          reconcilation_ending_date: "",
          csv: null,
        }}
        onSubmit={onSubmit}
        validationSchema={object({
          reconcilation_ending_date: string().required("Please select date"),
          csv: mixed().required("Please upload the Stripe balance summary"),
        })}
        validateOnMount
        enableReinitialize
      >
        {({
          values: { csv, reconcilation_starting_date },
          setFieldValue,
          isValid,
          isSubmitting,
          submitForm,
        }) => (
          <Form className="t-m-0 t-w-full">
            <Modal.Content>
              <Modal.Header>
                <ReconciliationHeader
                  brandName={brandName}
                  accountName={accountName}
                />
              </Modal.Header>
              <Modal.Body className="t-flex t-flex-col t-gap-4">
                <div>
                  <Label required>Reconciliation period</Label>
                  <div className="t-flex t-justify-between t-flex-row t-gap-2 t-items-center">
                    <div className="t-flex-1 sm:t-min-w-0">
                      <DateField
                        name="reconcilation_starting_date"
                        dateProps={{
                          customSize: "small",
                          portalId: "stripe-recon-start",
                          disabled: true,
                        }}
                      />
                    </div>
                    <span className="t-text-text-30">
                      <RightArrow />
                    </span>
                    <div className="t-flex-1 sm:t-min-w-0">
                      <DateField
                        name="reconcilation_ending_date"
                        dateProps={{
                          placeholder: "Select end date",
                          customSize: "small",
                          showErrorOnceTouched: true,
                          portalId: "stripe-recon-end",
                          maxDate: new Date(),
                          minDate: new Date(reconcilation_starting_date),
                          rightComponent: (
                            <span className="t-text-text-30">
                              <CalendarIcon />
                            </span>
                          ),
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <FileInput
                    name="csv"
                    withForm
                    label="Stripe balance summary"
                    required
                    onDelete={() => setFieldValue("csv", null)}
                    file={
                      csv && {
                        name: csv.name,
                        file_type: PDF,
                        is_previewable: false,
                        uuid: "",
                      }
                    }
                    accept={STRIPE_STATEMENT_ACCEPTED_FILES}
                  />
                  <p className="t-m-0 t-mt-2 t-text-body-sm t-text-text-60">
                    In Stripe, go to Reports &gt; Balance summary, pick the same
                    period and download the PDF.
                  </p>
                </div>
              </Modal.Body>
              <Modal.Footer className="t-flex t-items-center t-justify-end t-gap-3">
                <Button type="reset" onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  customType="primary"
                  type="submit"
                  disabled={!isValid || isSubmitting}
                  isLoading={isSubmitting}
                  // modal content renders in a portal, outside the form element
                  onClick={submitForm}
                >
                  Reconcile
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Form>
        )}
      </Formik>
    </Modal.Root>
  );
};
